<template>
  <v-modal name="transaction" class="modal" height="auto" :width="450" close-button scrollable
    @before-open="onBeforeOpen">
    <in-app-product-header :icon="transaction.icon_uri" :name="transaction.name"
      :description="transaction.description" />

    <ul class="info">
      <li>
        <strong>{{ $t('STATUS') }}</strong>
        <span class="status g-status"
          :class="/refunded/.test(transaction.status) ? 'g-status_error' : 'g-status_success'">
          {{ transaction.status_translated }}
        </span>
      </li>
      <li v-if="subscription">
        <strong>{{ $t('ASSOCIATED_SUBSCRIPTION') }}</strong>
        <span>
          <a :href="subscriptionUrl" @click.prevent="$modal.show('subscription', { data: subscription })">{{ $t('VIEW')
          }}</a>
        </span>
      </li>
      <li>
        <strong>{{ $t('DATE') }}</strong>
        <span>{{ new Date(transaction.added_at * 1000).toLocaleString() }}</span>
      </li>
      <li>
        <strong>{{ $t('TYPE') }}</strong>
        <span>{{ transaction.type_translated }}</span>
      </li>
      <li v-if="transaction.license_type === 'per_device'">
        <strong>{{ $t('DEVICE') }}</strong>
        <span>{{ transaction.nice_device_model }}</span>
      </li>
      <li>
        <strong>{{ $t('PRICE') }}</strong>
        <span>{{ formatCurrency(Math.abs(transaction.balance_change_cents_eur)) }}</span>
      </li>
      <li>
        <strong>{{ $t('INVOICE') }}</strong>
        <a :href="transaction.invoice_uri" target="_blank">{{ $t('VIEW') }}</a>
      </li>
      <li v-if="transaction.tos_text">
        <strong>{{ $t('TOS') }}</strong>
        <span><button class="g-pseudo-link"
            @click.prevent="$modal.show('markdown-document', transaction.tos_text)">View</button></span>
      </li>
      <li>
        <a :href="transaction.support_uri" target="_blank">{{ $t("GET_SUPPORT") }}</a>
        <template v-if="transaction.status === 'ok'">
          |
          <component :is="transaction.refund_uri ? 'a' : 'button'" class="g-pseudo-link refund-button"
            :href="transaction.refund_uri" @click.prevent="refundTransaction">
            <span>
              {{ $t('REQUEST_REFUND') }}
            </span>
            <loader v-if="isRefunding" class="refund-button__loader" size="13px" border="3px" />
          </component>
        </template>
      </li>
    </ul>
  </v-modal>
</template>

<script>
import VModal from '@/components/VModal.vue';
import InAppProductHeader from '@/components/InAppProductHeader.vue';
import formatCurrency from '@/utils/formatCurrency';
import Loader from '@/components/Loader';

export default {
  components: {
    VModal,
    InAppProductHeader,
    Loader,
  },
  data() {
    return {
      isCancelling: false,
      isRefunding: false,
      transaction: {},
      subscription: undefined,
      pollingTimeout: null,
    };
  },
  computed: {
    expirationLabel() {
      if (this.transaction.status === 'active') {
        return this.$t('SUBSCRIPTION_RENEWS');
      }

      if (Date.now() < new Date(this.transaction.expires_at * 1000)) {
        return this.$t('SUBSCRIPTION_EXPIRES');
      }

      return this.$t('SUBSCRIPTION_EXPIRED');
    },
    subscriptionUrl() {
      return `/my/billing?subscriptions&subscription=${this.subscription.purchase_identifier}`;
    },
  },
  methods: {
    formatCurrency,
    onBeforeOpen($event) {
      this.transaction = $event.params.transaction;
      this.subscription = $event.params.subscription;
    },
    async cancelSubscription() {
      await this.$confirm(this.$t('SUBSCRIPTION_CANCEL_CONFIRMATION'), { cancelText: this.$t('NO'), okText: this.$t('YES_CANCEL') });
      window.location.href = this.transaction.cancel_uri;
    },
    async refundTransaction() {
      if (this.isRefunding) {
        return;
      }

      await this.$confirm(this.$t('TRANSACTION_REFUND_CONFIRMATION'), { cancelText: this.$t('NO'), okText: this.$t('YES_REFUND') });
      if (this.transaction.refund_uri) {
        window.location.href = this.transaction.refund_uri;
        return;
      }

      this.isRefunding = true;
      try {
        await this.$api.requestRefund({ identifier: this.transaction.identifier });
        this.pollTransactionStatus();
      } catch {
        this.isRefunding = false;
      }
    },
    async pollTransactionStatus() {
      const interval = 1000;
      const timeout = 30000;
      const endTime = Date.now() + timeout;

      const checkStatus = async () => {
        if (Date.now() > endTime) {
          this.isCancelling = false;
          this.$notify({
            type: 'error',
            text: this.$t('TRANSACTION_REFUND_FAILED'),
          });
          return;
        }

        try {
          const transactions = await this.$api.getTransactions();
          const currentTransaction = transactions.find(trans => trans.identifier === this.transaction.identifier);

          if (currentTransaction.status === 'refunded') {
            this.isCancelling = false;
            this.transaction = currentTransaction;
            this.$notify({
              type: 'success',
              text: this.$t('TRANSACTION_REFUNDED'),
            });
            this.$emit('transaction-updated', currentTransaction);
            return;
          }
        } catch {
          //
        }

        this.pollingTimeout = setTimeout(checkStatus, interval);
      };

      checkStatus();
    },
    stopPolling() {
      if (this.pollingTimeout) {
        clearTimeout(this.pollingTimeout);
        this.pollingTimeout = null;
      }
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style lang="scss" scoped>
.modal {
  padding: 15px;
}

.info {
  margin-top: 15px;

  li:not(:last-child) {
    margin-bottom: 10px;
  }

  strong:first-child {
    margin-right: 5px;
  }
}

.status {
  font-size: 14px;
  word-break: break-word;
}

.refund-button {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &__loader {
    width: auto;
  }
}
</style>
